import React from "react";
import * as Icons from "lucide-react";
import { Check, ArrowRight, Phone, Star, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "../components/ui/accordion";
import BookingForm from "../components/BookingForm";
import Reveal from "../components/Reveal";
import { company, stats } from "../mock";
import useSeo from "../hooks/useSeo";

export default function LandingPage({ content }) {
  useSeo(content.seo);
  const scrollForm = () => document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <main className="pt-20">
      {/* HERO */}
      <section className="relative bg-ink overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-gold/15 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 -left-24 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-gold">
                <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {content.hero.badge}
              </span>
              <h1 className="mt-5 font-display font-700 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white">
                {content.hero.titlePre}<span className="gold-text">{content.hero.titleGold}</span>
              </h1>
              <p className="mt-5 text-lg text-white/70 max-w-xl leading-relaxed">{content.hero.subtitle}</p>

              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
                {content.hero.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2.5 text-[15px] font-medium text-white">
                    <span className="grid place-items-center h-6 w-6 rounded-full bg-gold text-ink shrink-0"><Check size={14} /></span>
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button onClick={scrollForm} className="h-12 px-8 rounded-full bg-gold hover:bg-gold-dark text-ink font-semibold text-base shadow-[0_8px_22px_rgba(201,162,74,0.4)]">
                  Book a Free Site Visit
                </Button>
                <a href={company.phoneHref}>
                  <Button variant="outline" className="h-12 px-8 rounded-full border-white/40 text-white bg-transparent hover:bg-white hover:text-ink font-semibold text-base">
                    <Phone size={18} className="mr-2" /> Call {company.phone}
                  </Button>
                </a>
              </div>
              <div className="mt-6 flex items-center gap-2 text-sm text-white/60">
                <span className="flex text-gold">{[...Array(5)].map((_, i) => <Star key={i} size={15} fill="currentColor" />)}</span>
                Rated 4.8/5 by businesses country-wide
              </div>
            </div>

            {/* Form */}
            <div id="lead-form" className="lg:sticky lg:top-28">
              <div className="rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] overflow-hidden">
                <div className="bg-gold px-6 py-5">
                  <p className="text-ink/70 text-xs font-semibold uppercase tracking-widest">Free Site Visit</p>
                  <h2 className="mt-1 font-display text-2xl text-ink">{content.hero.formTitle}</h2>
                  <p className="mt-1 text-sm text-ink/70">{content.hero.formSubtitle}</p>
                </div>
                <div className="p-6"><BookingForm source={content.source} /></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-gold-soft/50 border-y border-gold/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-4xl gold-text">{s.value}{s.suffix}</div>
              <div className="mt-1.5 text-xs uppercase tracking-widest text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl text-ink">{content.intro.title}</h2>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{content.intro.body}</p>
          </Reveal>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-gold-soft/40 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl text-ink">{content.benefitsTitle}</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.benefits.map((f, i) => {
              const Icon = Icons[f.icon] || Icons.Sparkles;
              return (
                <Reveal key={f.title} delay={i * 50}>
                  <div className="group h-full rounded-2xl border border-gold/20 bg-white p-7 hover:border-gold hover:shadow-xl transition-all duration-300">
                    <span className="grid place-items-center h-12 w-12 rounded-xl bg-ink text-gold group-hover:bg-gold group-hover:text-ink transition-colors">
                      <Icon size={22} />
                    </span>
                    <h3 className="mt-5 font-display text-lg text-ink">{f.title}</h3>
                    <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="bg-ink py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl text-white">How it works</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 70}>
                <div className="h-full rounded-2xl border border-white/10 bg-ink-soft p-7 hover:border-gold/50 transition-colors">
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-gold text-ink font-display text-xl font-700">{s.n}</span>
                  <h3 className="mt-5 font-display text-lg text-white">{s.title}</h3>
                  <p className="mt-2 text-[15px] text-white/60 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {content.testimonials.map((t, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="h-full rounded-2xl border border-gold/20 bg-gold-soft/40 p-7">
                <span className="flex text-gold">{[...Array(5)].map((_, k) => <Star key={k} size={16} fill="currentColor" />)}</span>
                <p className="mt-4 text-lg text-ink leading-relaxed">“{t.quote}”</p>
                <p className="mt-4 font-semibold text-ink">{t.name} <span className="font-normal text-muted-foreground">· {t.role}</span></p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gold-soft/40 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <h2 className="font-display text-3xl sm:text-4xl text-ink">Frequently asked questions</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="mt-8">
              {content.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-gold/20">
                  <AccordionTrigger className="text-left font-display text-lg text-ink hover:text-gold-dark hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[15px] text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-ink py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl text-white">{content.finalCta.title}</h2>
            <p className="mt-3 text-lg text-white/60">{content.finalCta.subtitle}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button onClick={scrollForm} className="h-12 px-8 rounded-full bg-gold hover:bg-gold-dark text-ink font-semibold text-base group">
                Book a Free Site Visit <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <a href={company.phoneHref}>
                <Button variant="outline" className="h-12 px-8 rounded-full border-white/40 text-white bg-transparent hover:bg-white hover:text-ink font-semibold text-base">
                  <Phone size={18} className="mr-2" /> Call {company.phone}
                </Button>
              </a>
            </div>
            <p className="mt-5 flex items-center justify-center gap-2 text-sm text-white/50">
              <ShieldCheck size={15} className="text-gold" /> No cost · No obligation · No lock-in contracts
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
